
import useTemplateBuilder from "./templateBuilder/useTemplateBuilder";
import useZoneWrapper from "./useZoneWrapper";
import { transformObjectToFlat } from "../utils/zone.utils";
import { useSubscribeDOMDestroyed, useSubscribeDOMRendered } from "./observer/useDomObserver";
import useWeakMap from "./utils/useWeakMap";


const zoneCaching = useWeakMap();

const injectStyle = () => {
    const existed = document.head.querySelector('style[rcz-style]');
    if (existed) return;
    const style = document.createElement('style');
    style.setAttribute('rcz-style', '');

    style.innerHTML = `*.rcz-rendering{display:none !important;opacity:0 !important;}`
    document.head.appendChild(style);
}
injectStyle();


const { addClass, removeClass } = (() => {

    const add = (elem, className) => {
        if (!(elem instanceof HTMLElement)) return;
        elem.classList.add(className)
    }

    const remove = (elem, className) => {
        if (!(elem instanceof HTMLElement)) return;
        elem.classList.remove(className)
    }

    return {
        addClass: (elem, className) => {
            if (Array.isArray(elem)) {
                for (let i = 0; i < elem.length; i++) {
                    add(elem[i], className)
                }
            } else {
                add(elem, className)
            }
        },
        removeClass: (elem, className) => {
            if (Array.isArray(elem)) {
                for (let i = 0; i < elem.length; i++) {
                    remove(elem[i], className)
                }
            } else {
                remove(elem, className)
            }
        }
    }
})();

class ReactiveZone {

    #trackingObject = {
        o: null
    };
    #zoneChangesCallbacks = new Set();
    #zoneInitCallbacks = [];
    #zoneReadyCallbacks = [];
    #zoneDestroyCallbacks = [];
    #hasDiscarded = false;
    #hasReady = false;
    #hasInit = false;

    useZoneComputed = () => { }
    useElementRef = () => { }
    useZoneTransform = () => { }
    useElementRefList = () => { }

    /**
     * Description placeholder
     *
     * @type {'ready' | 'discarded' | 'nothing' }
     */
    #status = 'nothing';

    #zoneWrapper = useZoneWrapper(this.#trackingObject);

    constructor(trackingObject) {
        this.#trackingObject.o = trackingObject;
        this.#init();
    }

    #init() {

        // this.#injectMiddleware()
        const { middlewareOutput, onPropsChanges } = this.#zoneWrapper
        for (let k in middlewareOutput) {
            this[k] = middlewareOutput[k];
        }
        onPropsChanges(this.#emitChanges.bind(this));
    }

    #emitChanges(prop, oldValue, newValue, propertyPath) {
        if (this.#hasDiscarded) { return; }
        this.#zoneChangesCallbacks.forEach((fn) => {
            if (typeof fn === 'function') {
                try {
                    fn.call(null, { prop, oldValue, newValue, propertyPath })
                } catch (ex) {
                    console.warn('ZoneChanges', ex);
                }
            } else if (typeof fn === 'object' && typeof fn[prop] === 'function') {
                fn[prop].call(null, { prop, newValue, oldValue, })
            }
        })
    }

    #emitInit() {
        if (this.#status === 'discarded') { return; }
        while (this.#zoneInitCallbacks.length > 0) {
            const fn = this.#zoneInitCallbacks.shift();
            if (typeof fn === 'function') {
                try {
                    fn.call(null)
                } catch (ex) {
                    console.warn('ZoneReady', ex);
                    throw ex;
                }
            }
        }
    }

    #emitReady() {
        if (this.#status === 'discarded') { return; }
        while (this.#zoneReadyCallbacks.length > 0) {
            const fn = this.#zoneReadyCallbacks.shift();
            if (typeof fn === 'function') {
                try {
                    fn.call(null)
                } catch (ex) {
                    console.warn('ZoneInit', ex);
                }
            }
        }
    }

    #emitDestroy() {
        // if (this.#status === 'discarded') { return; }
        while (this.#zoneDestroyCallbacks.length > 0) {
            const fn = this.#zoneDestroyCallbacks.shift();
            if (typeof fn === 'function') {
                try {
                    fn.call(null)
                } catch (ex) {
                    console.warn('ZoneDestroy', ex);
                }
            }
        }
    }

    onZoneInit(fn) {
        if (this.#hasInit) {
            fn.call(null);
            return;
        }
        this.#zoneInitCallbacks.push(fn);
    }
    /**
     * Description placeholder
     *
     * @param { Object.<string,() =>void> | () => void } fn
     * @returns {() => void}
     */
    onZoneChanges(fn) {
        if (typeof fn === 'object') {
            fn = transformObjectToFlat(fn);
        }
        this.#zoneChangesCallbacks.add(fn);
        return () => {
            this.#zoneChangesCallbacks.delete(fn)
        }
    }

    onZoneReady(fn) {
        if (this.#hasReady) {
            fn.call(null);
            return;
        }
        this.#zoneReadyCallbacks.push(fn);
    }

    onZoneDestroy(fn) {
        if (this.#status === 'discarded') { return; }
        this.#zoneDestroyCallbacks.push(fn);
    }


    /**
     * @template T
     * @param {T} props 
     * @returns {T}
     */
    injectPropToZone(props) {
        if (this.#status === 'discarded') {
            console.warn("Zone has been discard");
            return;
        }

        const { injectProp } = this.#zoneWrapper
        if (typeof props !== 'object' || props === null) {
            throw new Error('Only object allowed - {key: any}');
        }
        const [l1, l2] = injectProp(props);
        return l1;
    }

    /**
     * Description placeholder
     *
     * @param {Object<string, Function>} methods
     */
    injectMethodToZone(methods) {
        const _methods = typeof methods === 'function' ? methods.call(null) : (methods || {});
        for (let k in _methods) {
            if (typeof _methods[k] !== 'function') {
                throw new Error('[injectMethodToZone] Only method allowed')
            }
        }
        const { injectMethod } = this.#zoneWrapper;
        injectMethod(methods);
    }

    refreshProp(propNaming) {
        const { zonePropsUI } = this.#zoneWrapper;
        zonePropsUI.refresh(propNaming)
    }


    useTemplate(options) {
        if (this.#hasInit) {
            console.warn("zone already init");
            return;
        }

        const { zonePropsUI, zoneProp } = this.#zoneWrapper;
        const { applyRoot, template } = options;

        if (!applyRoot) {
            // throw new Error(`applyRoot is null`);
            return
        }


        addClass(applyRoot, 'rcz-rendering');
        this.#hasInit = true;

        this.#emitInit();
        setTimeout(() => {
            useTemplateBuilder({
                rootElement: applyRoot,
                template: template,
                props: zonePropsUI
            }, () => {
                removeClass(applyRoot, 'rcz-rendering')
                this.#emitReady()
            });
        });
        if (applyRoot instanceof HTMLElement) {
            useSubscribeDOMDestroyed(applyRoot, ()=>{
                this.discardZone()
            }, null, zoneProp, document.body)
        } else if (Array.isArray(applyRoot)) {
            let count = 0;
            for (let i in applyRoot) {
                useSubscribeDOMDestroyed(applyRoot[i], () => {
                    count++;
                    if (count == applyRoot.length) {
                        this.discardZone();
                    }
                }, null, zoneProp, document.body)
            }
        }
    }

    discardZone() {
        const { discardWrapper } = this.#zoneWrapper;
        this.#status = 'discarded';
        this.#zoneChangesCallbacks.clear();
        this.#trackingObject = null;
        discardWrapper();
        this.#emitDestroy()
        // zoneCaching.delete(this.#trackingObject.o);
    }

    canCollectGarbage() {
        const keys = Object.keys(this.#zoneWrapper.zoneProp);
        return keys.length === 0;
    }

}

export default ReactiveZone;